import React from 'react';
import { Link } from 'react-router-dom';

const LOOP_STEPS = [
  {
    n: '01',
    title: 'You spend time on the product',
    body: 'Talk, log, journal, play. Use the product the way you naturally would. Nothing about it feels like homework.',
  },
  {
    n: '02',
    title: 'We read the signal in the background',
    body: 'Every minute interaction — what you say, how often, when, your tone, your patterns — becomes structured data. You don\'t see it happening.',
  },
  {
    n: '03',
    title: 'We turn it into top-level insight',
    body: 'Personality, moods, recurring patterns, blind spots — surfaced as clean reports and clear narratives. Not metrics. Insight you can actually read.',
  },
  {
    n: '04',
    title: 'You decide what to work on',
    body: 'Armed with clarity, you choose what to fix. Optionally bring in a licensed professional — Bloora hands them the same picture you\'re looking at.',
  },
];

const USPS = [
  {
    eyebrow: 'USP 01',
    title: 'Reports & insights that bridge the data gap',
    body:
      'Mental health has historically been measured by 50 minutes of conversation, once a week, by feel. Bloora generates reports and longitudinal insights from data that was previously invisible — closing a gap the field has lived with for decades.',
  },
  {
    eyebrow: 'USP 02',
    title: 'Open Chat — most people don\'t need a clinic',
    body:
      'The Open Chat feature gives you a continuous, intelligent space to think, talk, and reflect. For the majority of people, that alone is enough. Clinical intervention is there when you want it — not as the only door in.',
  },
];

export default function Bloora() {
  return (
    <>
      {/* Hero */}
      <section className="px-6 pt-16 pb-24 md:pt-24 md:pb-32 background-grid">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-gray-500">A Cognitics Product</span>
            <h1 className="font-display text-5xl md:text-7xl font-normal tracking-[0.15em] mt-6 mb-6">
              BLOORA
            </h1>
            <p className="text-2xl md:text-3xl text-gray-700 leading-snug italic mb-6">
              Turning raw emotion into readable data.
            </p>
            <div className="w-24 h-px bg-gray-900 mb-8" />
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-2xl">
              Bloora solves mental health using data science. We turn your moods,
              your conversations, and your patterns into clear, readable insight —
              so you get clarity, you know yourself more than ever, and you work on
              yourself better.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/early-access" className="btn-primary">Join early access</Link>
              <a href="#how-it-works" className="btn-ghost">See how it works</a>
            </div>
          </div>

          <div className="flex lg:justify-end">
            <img
              src="/Bloora_logo2.png"
              alt="Bloora"
              className="h-48 md:h-72 w-auto drop-shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* The gap Bloora addresses */}
      <section className="py-24 md:py-32 px-6" style={{ backgroundColor: '#f5f6ed' }}>
        <div className="max-w-4xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">The Gap</span>
          <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6 leading-tight">
            Most mental health tools live at one of two extremes.
          </h2>
          <div className="w-20 h-px bg-gray-900 mb-10" />
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Journaling apps with no intelligence, or expensive therapy scheduling
            tools with no in-between. Bloora occupies the continuum: from
            self-reflection to professional clinical context, in one product.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Whether you're a 3am thinker, someone tracking patterns between
            sessions, or a person who wants a clinical-quality picture to hand to a
            therapist — Bloora is built for all three.
          </p>
        </div>
      </section>

      {/* How it works — the loop */}
      <section id="how-it-works" className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">How It Works</span>
          <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6 leading-tight">
            A loop you don't have to think about.
          </h2>
          <div className="w-24 h-px bg-gray-900 mb-10" />
          <p className="text-lg text-gray-700 leading-relaxed mb-16 max-w-3xl">
            You don't have to use Bloora "to fix yourself." Most products of this
            kind feel like homework. Bloora is built to be enjoyable — the work
            happens quietly, in the background, and the value comes back to you as
            insight you can actually use.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {LOOP_STEPS.map((s) => (
              <div
                key={s.n}
                className="p-8 border border-gray-200"
                style={{ backgroundColor: '#fbfcf3' }}
              >
                <div className="w-10 h-10 border border-gray-400 flex items-center justify-center text-sm font-light mb-6">
                  {s.n}
                </div>
                <h3 className="text-xl font-medium mb-3">{s.title}</h3>
                <p className="text-gray-700 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          <p className="mt-12 text-base md:text-lg text-gray-600 italic max-w-3xl">
            We fix you without making you realize we did.
          </p>
        </div>
      </section>

      {/* USPs */}
      <section className="py-24 md:py-32 px-6" style={{ backgroundColor: '#f5f6ed' }}>
        <div className="max-w-6xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Why Bloora Is Different</span>
          <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6 leading-tight">
            Two things no one else is doing well.
          </h2>
          <div className="w-24 h-px bg-gray-900 mb-16" />

          <div className="grid md:grid-cols-2 gap-10">
            {USPS.map((u) => (
              <div key={u.title}>
                <span className="text-xs uppercase tracking-[0.3em] text-gray-500">{u.eyebrow}</span>
                <h3 className="text-2xl md:text-3xl font-light mt-3 mb-4 leading-tight">{u.title}</h3>
                <div className="w-12 h-px bg-gray-900 mb-6" />
                <p className="text-lg text-gray-700 leading-relaxed">{u.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For users / for professionals — light two-sided */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Built For Two Sides</span>
          <h2 className="text-4xl md:text-5xl font-light mt-4 mb-6 leading-tight">
            One platform, two views.
          </h2>
          <div className="w-24 h-px bg-gray-900 mb-16" />

          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-10 border border-gray-200" style={{ backgroundColor: '#fbfcf3' }}>
              <span className="text-xs uppercase tracking-[0.3em] text-gray-500">For Users</span>
              <h3 className="text-2xl font-light mt-3 mb-4">A space that gets you</h3>
              <div className="w-12 h-px bg-gray-900 mb-6" />
              <p className="text-gray-700 leading-relaxed">
                Bloora is built around how you actually use it — conversation,
                reflection, mood. The product earns your time. The insights show up
                as a side effect.
              </p>
            </div>

            <div className="p-10 border border-gray-200" style={{ backgroundColor: '#fbfcf3' }}>
              <span className="text-xs uppercase tracking-[0.3em] text-gray-500">For Professionals</span>
              <h3 className="text-2xl font-light mt-3 mb-4">A picture, on day one</h3>
              <div className="w-12 h-px bg-gray-900 mb-6" />
              <p className="text-gray-700 leading-relaxed">
                When a user chooses to bring in a clinician, the clinician opens to
                the same insights the user is looking at — not a blank page. Data
                access is granted per professional, and revocable any time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Early access CTA */}
      <section className="py-24 md:py-28 px-6" style={{ backgroundColor: '#f5f6ed' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-light mb-6 leading-tight">
            Bloora is in early access.
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-10 max-w-2xl">
            We're inviting the first wave of users — and a small group of
            clinicians and researchers — to shape the product before it opens up.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/early-access" className="btn-primary">Join early access →</Link>
            <Link to="/research" className="btn-ghost">See the research behind it</Link>
          </div>
        </div>
      </section>
    </>
  );
}
